import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";
import { CHART_DASH, PROJECTED_DASH, colorFor } from "./loss-development-chart";

const AXIS_COLOR = "var(--neutral-inactive)";

type Row = { month: string; [key: string]: number | string | null };

function CohortTooltip({
  active,
  payload,
  label,
  xTitle,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number | null; color: string }>;
  label?: string;
  xTitle: string;
}) {
  if (!active || !payload?.length) return null;
  const seen = new Set<string>();
  const entries = payload.filter((entry): entry is { name: string; value: number; color: string } => {
    if (entry.value == null || seen.has(entry.name)) return false;
    seen.add(entry.name);
    return true;
  });
  if (!entries.length) return null;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">
        {xTitle} {label}
      </p>
      <div className="mt-1 space-y-0.5">
        {entries.map((entry) => (
          <p key={entry.name} className="flex items-center gap-2 text-muted-foreground">
            <span className="size-2" style={{ backgroundColor: entry.color }} aria-hidden />
            {entry.name}
            <span className="ml-auto tabular-nums text-foreground">{entry.value.toFixed(2)}%</span>
          </p>
        ))}
      </div>
    </ChartTooltipShell>
  );
}

/**
 * One line set per funded-year cohort, drawn exactly like the Loss Development chart on Loss Calibration
 * (loss-development-chart.tsx), for every chart that has realized readings and a projected stretch:
 *
 *   - realized readings: the cohort's own colour, solid, a dot on each reading;
 *   - past the last realized reading: the published curve as stored, fine-dotted, joined to the last reading;
 *   - the model's projection at every month, longer-dashed and a touch thicker (omitted when `projection` is not given,
 *     e.g. the PD chart has no separate projection series);
 *   - the newest cohort is drawn thicker; the colours follow the cohort's place in `allYears`, so a cohort keeps its
 *     colour as the year chips and the Realized / Projected / Active toggle change what is shown;
 *   - a legend row below names each cohort, and says what solid and dotted mean.
 *
 * `rows` holds the published curve (key = `${keyPrefix}${year}`), `projection` the projected values in the same shape,
 * `realized` maps each year to the month labels that are realized readings (`consider` true). The toggle only picks which
 * rows of the table the caller fetched; `consider` alone decides actual (solid) versus projected (dotted) per month.
 */
export function CohortDevelopmentChart({
  rows,
  projection,
  realized,
  years,
  allYears,
  keyPrefix = "",
  xTitle = "Months",
  xAxisLabel,
  height = 300,
  referenceLine,
}: {
  rows: Row[];
  projection?: Row[];
  realized: Record<string, string[]>;
  /** The cohorts to draw. */
  years: string[];
  /** Every cohort, in order: the palette follows a cohort's position here. */
  allYears: string[];
  keyPrefix?: string;
  xTitle?: string;
  xAxisLabel?: string;
  height?: number;
  referenceLine?: { value: number; label: string };
}) {
  const palette = allYears.map((_, i) => i);
  const newest = years[years.length - 1];
  const widthFor = (year: string) => (year === newest ? 2.75 : 1.75);

  // x entries: the published curve's months, then any month only the projection has
  const months = rows.map((r) => r.month);
  for (const r of projection ?? []) if (!months.includes(r.month)) months.push(r.month);
  const byMonth = new Map(rows.map((r) => [r.month, r]));
  const projectionByMonth = new Map((projection ?? []).map((r) => [r.month, r]));

  const data: Row[] = months.map((month) => ({ month }));
  for (const year of years) {
    const key = `${keyPrefix}${year}`;
    const real = new Set(realized[year] ?? []);
    let lastRealized: Row | undefined;
    for (const row of data) {
      const value = byMonth.get(row.month)?.[key];
      const chartValue = typeof value === "number" ? value : null;
      const isRealized = chartValue !== null && real.has(row.month);
      row[`a${year}`] = isRealized ? chartValue : null;
      row[`c${year}`] = chartValue !== null && !isRealized ? chartValue : null;
      const projected = projectionByMonth.get(row.month)?.[key];
      row[`p${year}`] = typeof projected === "number" ? projected : null;
      if (isRealized) lastRealized = row;
    }
    // the fine-dotted stretch starts where the realized line ends
    if (lastRealized && data.some((row) => row[`c${year}`] != null)) {
      lastRealized[`c${year}`] = lastRealized[`a${year}`] as number;
    }
  }

  return (
    <div className="w-full">
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: xAxisLabel ? 24 : 8, left: 0 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fill: AXIS_COLOR, fontSize: 11 }}
              stroke={AXIS_COLOR}
              tickLine={false}
              {...(xAxisLabel
                ? {
                    label: {
                      value: xAxisLabel,
                      position: "insideBottom",
                      offset: -14,
                      fill: AXIS_COLOR,
                      fontSize: 11,
                    },
                  }
                : {})}
            />
            <YAxis
              tickFormatter={(value: number) => `${value.toFixed(1)}%`}
              tick={{ fill: AXIS_COLOR, fontSize: 11 }}
              stroke={AXIS_COLOR}
              width={56}
              // keep a reference threshold on-chart even when every line sits below it
              {...(referenceLine
                ? { domain: [0, (dataMax: number) => Math.ceil(Math.max(dataMax, referenceLine.value * 1.1) * 10) / 10] }
                : {})}
            />
            <Tooltip content={<CohortTooltip xTitle={xTitle} />} />
            {referenceLine ? (
              <ReferenceLine
                y={referenceLine.value}
                stroke="var(--decline)"
                strokeDasharray="4 4"
                label={{ value: referenceLine.label, position: "insideTopLeft", fill: "var(--decline)", fontSize: 11 }}
              />
            ) : null}
            {years.map((year) => (
              <Line
                key={`a${year}`}
                type="monotone"
                dataKey={`a${year}`}
                name={`${year} actual`}
                stroke={colorFor(allYears.indexOf(year), palette)}
                strokeWidth={widthFor(year)}
                dot={{ r: 2.5 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
            {years.map((year) => (
              <Line
                key={`c${year}`}
                type="monotone"
                dataKey={`c${year}`}
                name={`${year} actual`}
                stroke={colorFor(allYears.indexOf(year), palette)}
                strokeWidth={widthFor(year)}
                strokeDasharray={CHART_DASH}
                strokeLinecap="round"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
            {projection
              ? years.map((year) => (
                  <Line
                    key={`p${year}`}
                    type="monotone"
                    dataKey={`p${year}`}
                    name={`${year} projected`}
                    stroke={colorFor(allYears.indexOf(year), palette)}
                    strokeWidth={widthFor(year) + 0.75}
                    strokeDasharray={PROJECTED_DASH}
                    strokeLinecap="round"
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))
              : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {years.map((year) => (
          <span key={year} className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-4"
              style={{ backgroundColor: colorFor(allYears.indexOf(year), palette) }}
              aria-hidden
            />
            {year}
          </span>
        ))}
        <span className="ml-auto">Solid = actual · dotted = projected</span>
      </div>
    </div>
  );
}
